// packages/web/src/components/Common/CourtSearchSelect.tsx
import React, { useState, useEffect, useId } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  SelectChangeEvent,
  FormHelperText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import { Court, AlgoliaSearchService } from '@matchpoint/shared';
import { initializeAlgoliaForWeb } from '../../utils/algoliaInit';

const CompactFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => !['isMobile', 'isTablet'].includes(prop as string),
})<{ isMobile?: boolean; isTablet?: boolean }>(({ theme, isMobile, isTablet }) => ({
  // Tablet: Smaller form controls
  ...(isTablet && !isMobile && {
    '& .MuiInputLabel-root': {
      fontSize: '0.85em',
    },
    '& .MuiSelect-select': {
      fontSize: '0.85em',
      padding: theme.spacing(0.75, 1.25),
    },
  }),
  
  // Mobile: Normal size in drawer
  ...(isMobile && {
    '& .MuiInputLabel-root': {
      fontSize: '0.9em',
    },
    '& .MuiSelect-select': {
      fontSize: '0.9em',
      padding: theme.spacing(1, 1.5),
    },
  }),
}));

export interface CourtSearchSelectProps {
  value: string;
  onChange: (value: string, court?: Court) => void;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  isMobile?: boolean;
  isTablet?: boolean;
  includeAnyOption?: boolean;
  anyOptionLabel?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  error?: boolean;
}

export const CourtSearchSelect: React.FC<CourtSearchSelectProps> = ({
  value = '',  // Default to empty string
  onChange,
  label = 'Court',
  size = 'medium',
  fullWidth = true,
  isMobile = false,
  isTablet = false,
  includeAnyOption = true,
  anyOptionLabel = 'Any Court',
  disabled = false,
  required = false,
  helperText,
  error = false,
}) => {
  const [courts, setCourts] = useState<Court[]>([]);
  const [loadingCourts, setLoadingCourts] = useState(false);
  const [courtSearchQuery, setCourtSearchQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedCourt, setSelectedCourt] = useState<Court | null>(null);
  
  // Track whether this component uses 'any' or empty string for "Any Court"
  const [usesAnyValue, setUsesAnyValue] = useState(value === 'any');
  
  // Generate unique IDs for accessibility
  const selectId = useId();
  const labelId = `${selectId}-label`;

  // Update usesAnyValue when value prop changes
  useEffect(() => {
    if (value === 'any') {
      setUsesAnyValue(true);
    }
  }, [value]);

  // Convert value to be compatible with MUI Select
  // Empty string shows placeholder, 'any' is a valid selection
  const normalizeValue = (inputValue: string): string => {
    if (!inputValue) {
      return '';
    }
    return inputValue;
  };

  // Simply use the normalized value without validation
  // The parent component should handle validation if needed
  const selectValue = normalizeValue(value);

  // Load specific court details when value changes
  useEffect(() => {
    const loadSelectedCourt = async () => {
      if (!selectValue || selectValue === '') {
        setSelectedCourt(null);
        return;
      }

      // Check if court is already in the loaded courts
      const existingCourt = courts.find(c => c.id === selectValue);
      if (existingCourt) {
        setSelectedCourt(existingCourt);
        return;
      }

      // If not found and we have a court ID, try to load it
      try {
        await initializeAlgoliaForWeb();
        const algoliaService = AlgoliaSearchService.getInstance();
        
        // Search specifically for this court ID
        const searchParams = {
          query: '',
          page: 0,
          hitsPerPage: 100,
          filters: `objectID:${selectValue}`,
          facets: ['location.city', 'location.country', 'details.facility_type', 'details.court_surface', 'pricing.category'],
        };

        const result = await algoliaService.searchCourts(searchParams);
        const court = result.courts.find(c => c.id === selectValue);
        
        if (court) {
          setSelectedCourt(court);
          // Also add it to courts array so it appears in dropdown
          setCourts(prev => {
            const exists = prev.some(c => c.id === court.id);
            return exists ? prev : [court, ...prev];
          });
        }
      } catch (error) {
        console.error('Error loading selected court:', error);
      }
    };

    loadSelectedCourt();
  }, [selectValue]);

  // Load courts using Algolia search when court search query changes or menu opens
  useEffect(() => {
    const loadCourts = async () => {
      if (!menuOpen) {
        return;
      }

      setLoadingCourts(true);
      
      try {
        // Initialize Algolia first
        await initializeAlgoliaForWeb();
        
        const algoliaService = AlgoliaSearchService.getInstance();
        
        const searchParams = {
          query: courtSearchQuery,
          page: 0,
          hitsPerPage: 100, // Allow more scrolling options
          filters: '', // No filters for court dropdown
          facets: [],
        };

        const result = await algoliaService.searchCourts(searchParams);
        setCourts(result.courts);
        console.log('CourtSearchSelect: Loaded courts from Algolia:', result.courts.length);
      } catch (error) {
        console.error('CourtSearchSelect: Error loading courts from Algolia:', error);
        setCourts([]);
      } finally {
        setLoadingCourts(false);
      }
    };
    
    // Debounce the search to avoid too many API calls
    const timeoutId = setTimeout(loadCourts, 300);
    return () => clearTimeout(timeoutId);
  }, [courtSearchQuery, menuOpen]);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value;
    
    if (selectedValue && selectedValue !== '') {
      // Find the court object from the loaded courts
      const selectedCourt = courts.find(court => court.id === selectedValue);
      onChange(selectedValue, selectedCourt);
    } else {
      // No court selected (Any Court)
      onChange(selectedValue);
    }
  };

  const handleMenuOpen = () => {
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    // Reset search when closing - important for test compatibility
    setCourtSearchQuery('');
  };

  return (
    <CompactFormControl 
      fullWidth={fullWidth}
      size={size}
      isMobile={isMobile}
      isTablet={isTablet}
      disabled={disabled}
      required={required}
      error={error}
    >
      <InputLabel id={labelId} shrink={true}>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        id={selectId}
        value={selectValue}
        onChange={handleSelectChange}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        label={label}
        // Ensure the select shows a value even when courts haven't loaded
        displayEmpty
        // Add renderValue to properly display "Any Court" when includeAnyOption is true
        renderValue={(value) => {
          // If no value and includeAnyOption is true, show the "Any Court" label
          if (!value || value === '') {
            if (includeAnyOption) {
              return <span>{anyOptionLabel}</span>;
            }
            return <em style={{ color: 'text.secondary' }}>Select a court...</em>;
          }
          if (value === 'any') {
            return <span>{anyOptionLabel}</span>;
          }
          
          // First check selectedCourt state
          let court = selectedCourt && selectedCourt.id === value ? selectedCourt : null;
          
          // If not found, check in loaded courts
          if (!court) {
            court = courts.find(c => c.id === value) || null;
          }
          
          // If we have court details, show full information
          if (court) {
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                  {court.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {court.location.address}, {court.location.city}
                  {court.pricing?.hourly ? ` ${court.pricing.hourly}` : 
                   court.details?.court_fees === "Yes" ? ' Paid' : ' Free'}
                </Typography>
              </Box>
            );
          }
          
          // Fallback to just the ID
          return `Court ID: ${value}`;
        }}
        // Add this to ensure label behavior is correct
        inputProps={{
          'aria-label': label,
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
              width: 'auto',
            },
          },
          // Add these props to improve test compatibility
          disablePortal: false,
          keepMounted: false,
        }}
      >
        {/* Search field at the top of dropdown */}
        <MenuItem disableRipple style={{ backgroundColor: 'transparent' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search courts..."
            value={courtSearchQuery}
            onChange={(e) => {
              e.stopPropagation();
              setCourtSearchQuery(e.target.value);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            InputProps={{
              style: { fontSize: '0.875rem' }
            }}
          />
        </MenuItem>
        
        {/* Any Court option - use appropriate value based on context */}
        {includeAnyOption && (
          <MenuItem value={usesAnyValue ? 'any' : ''}>
            <strong>{anyOptionLabel}</strong>
          </MenuItem>
        )}
        
        {/* If we have a selected court ID but courts haven't loaded, show a placeholder */}
        {selectValue && selectValue !== '' && (!courts || courts.length === 0) && !loadingCourts && (
          <MenuItem value={selectValue} disabled>
            <Box sx={{ py: 1, fontStyle: 'italic', color: 'text.secondary' }}>
              Court ID: {selectValue} (Loading details...)
            </Box>
          </MenuItem>
        )}
        
        {/* If we have a selected court ID that's not in the loaded results, show it */}
        {selectValue && selectValue !== '' && courts && courts.length > 0 && !courts.some(c => c.id === selectValue) && (
          <MenuItem value={selectValue}>
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                Selected Court
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ID: {selectValue}
              </Typography>
            </Box>
          </MenuItem>
        )}
        
        {loadingCourts ? (
          <MenuItem value="loading" disabled>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={16} />
              Loading courts...
            </Box>
          </MenuItem>
        ) : !courts || courts.length === 0 ? (
          <MenuItem value="none" disabled>
            <Box sx={{ py: 1, fontStyle: 'italic', color: 'text.secondary' }}>
              {courtSearchQuery ? `No courts found for "${courtSearchQuery}"` : 'No courts available'}
            </Box>
          </MenuItem>
        ) : (
          courts.map((court) => (
            <MenuItem key={court.id} value={court.id}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', py: 0.5 }}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {court.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {court.location.address}, {court.location.city}
                    {court.pricing?.hourly ? (
                      <span style={{ marginLeft: 8, fontWeight: 500 }}>
                        ${court.pricing.hourly}
                      </span>
                    ) : court.details?.court_fees === "Yes" ? (
                      <span style={{ marginLeft: 8, fontWeight: 500 }}>
                        Paid
                      </span>
                    ) : (
                      <span style={{ marginLeft: 8, color: 'green' }}>
                        Free
                      </span>
                    )}
                  </Typography>
                </Box>
                <Tooltip title="View court details">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      window.open(`/courts/${court.id}`, '_blank');
                    }}
                    sx={{ ml: 1 }}
                    aria-label="View court details"
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </MenuItem>
          ))
        )}
      </Select>
      {helperText && (
        <FormHelperText error={error}>
          {helperText}
        </FormHelperText>
      )}
    </CompactFormControl>
  );
};

export default CourtSearchSelect;